import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import Iconify from '../../theme/iconify/iconify';

import Pagination from '../util/Pagination';
import ConfirmationModal from '../util/ConfirmationModal';

function Sentences({ document, setDocument, update }) {
    const [value, setValue] = React.useState(document.raw_sentences_text[0]);
    const [rows, setRows] = React.useState(5);
    const [numSentences, setNumSentences] = React.useState(document.raw_sentences_text.length);
    const [sentenceIndex, setSentenceIndex] = React.useState(1);
    const [removeSentenceModal, setRemoveSentenceModal] = React.useState(false);

    const { t } = useTranslation();

    React.useEffect(() => {
        if (!document) return;
        if (!sentenceIndex) return;
        setValue(document.raw_sentences_text[sentenceIndex - 1]);
    }, [document, sentenceIndex]);

    React.useEffect(() => {
        setNumSentences(document.raw_sentences_text.length);
    }, [document]);

    function changePage(offset) {
        setSentenceIndex(prevPageNumber => prevPageNumber + offset);
    }

    const removeSentence = () => {
        const rawSentences = [...document.raw_sentences_text];
        rawSentences.splice(sentenceIndex - 1, 1);
        if (sentenceIndex === numSentences) changePage(-1);
        setDocument({ ...document, raw_sentences_text: rawSentences });
        update({
            _id: document._id,
            original: document.original,
            filename: document.filename,
            type: document.type,
            size: document.size,
            timestamp: document.timestamp,
            user: document.user,
            raw_pages_text: document.raw_pages_text,
            raw_sentences_text: rawSentences,
            embeddings: document.embeddings
        });
        setRemoveSentenceModal(false);
    };

    return (
        <>
            {document.raw_sentences_text.length !== 0 ?
                <Grid container justifyContent='center' alignItems='center'>
                    <Grid item xs={12} sx={{ position: 'relative' }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={rows}
                            value={value}
                            onChange={(e) => {
                                setValue(e.target.value);
                                const rawSentences = [...document.raw_sentences_text];
                                rawSentences[sentenceIndex - 1] = e.target.value;
                                setDocument({ ...document, raw_sentences_text: rawSentences });
                            }}
                        />
                        <Grid
                            container
                            justifyContent='flex-end'
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                mt: 2,
                                mr: 2,
                                pb: 2,
                                zIndex: 1,
                                width: 'fit-content',
                                opacity: {
                                    xs: 1,
                                    lg: 0.4
                                },
                                '&:hover': {
                                    opacity: 1
                                }
                            }}
                        >
                            <Paper elevation={2} sx={{ border: (theme) => `solid 1px ${theme.palette.divider}` }}>
                                <Tooltip title={t('document.tabs.sentences.tooltips.remove')} arrow>
                                    <IconButton
                                        color='error'
                                        onClick={() => setRemoveSentenceModal(true)}
                                    >
                                        <Iconify icon='eva:trash-outline' />
                                    </IconButton>
                                </Tooltip>
                                <Box
                                    component='span'
                                    sx={{
                                        display: 'inline',
                                        alignSelf: 'center',
                                        fontSize: '0.875rem',
                                        borderRight: (theme) => `solid 2px ${theme.palette.divider}`,
                                    }}
                                />
                                <Tooltip title={t('document.tabs.sentences.tooltips.save')} arrow>
                                    <IconButton
                                        color='primary'
                                        onClick={() => update(document)}
                                    >
                                        <Iconify icon='carbon:save' />
                                    </IconButton>
                                </Tooltip>
                            </Paper>
                        </Grid>
                        <Pagination page={sentenceIndex} total={numSentences} changePage={changePage} />
                    </Grid>
                </Grid>
                :
                <Grid item xs={12}>
                    <Typography variant='body2'>{t('document.noSentences')}</Typography>
                </Grid>
            }
            <ConfirmationModal
                type='remove'
                title={t('document.modals.removeSentence.title')}
                description={t('document.modals.removeSentence.description')}
                confirmationText={t('document.modals.removeSentence.confirmation')}
                confirm={removeSentence}
                open={removeSentenceModal}
                onClose={() => setRemoveSentenceModal(false)}
            />
        </>
    );
}

export default Sentences;